import React, {useEffect, useRef, useState} from 'react'
import {useTimer} from 'react-timer-hook';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import {useCompleteSession, useStartSession} from "./hooks/useSession";
import {POMODORO_STATE_STORAGE_KEY, readPersistedTimerState} from "./timerStorage";

const audio = new Audio('/sounds/endsession-v1.mp3');
const sessionAudio = new Audio('/session-sounds/waterfall/waterfall-v2.mp3');
sessionAudio.loop = true
const DEFAULT_BREAK_DURATION_SECONDS = 300

function createExpiryTimestamp(durationSeconds) {
    const time = new Date()
    time.setSeconds(time.getSeconds() + durationSeconds)
    return time
}

function PomodoroTimer({
    expiryTimestamp,
    defaultDurationSeconds = 1500,
    breakDurationSeconds = DEFAULT_BREAK_DURATION_SECONDS,
    isSettingsLoading = false,
    task,
    project,
    authToken,
    onSessionStart,
    onSessionComplete,
    breakTasks = [],
}) {
    const safeDefaultDurationSeconds = Math.max(1, Math.trunc(defaultDurationSeconds || 1500))
    const safeBreakDurationSeconds = Math.max(60, Math.trunc(breakDurationSeconds || DEFAULT_BREAK_DURATION_SECONDS))
    const [phase, setPhase] = useState('idle')
    const [isMuted, setMuted] = useState(true)
    const [completionError, setCompletionError] = useState('')
    const [isCompleting, setIsCompleting] = useState(false)
    const [timerDurationSeconds, setTimerDurationSeconds] = useState(() => safeDefaultDurationSeconds)
    const [completedRounds, setCompletedRounds] = useState(0)
    const [hasHydratedState, setHasHydratedState] = useState(false)
    const [selectedBreakTask, setSelectedBreakTask] = useState(null)
    const currentSessionIdRef = useRef(null)
    const phaseRef = useRef(phase)
    const defaultDurationRef = useRef(safeDefaultDurationSeconds)
    const hydrationInitializedRef = useRef(false)
    const {mutateAsync: startSession} = useStartSession(authToken)
    const {mutateAsync: completeSession} = useCompleteSession(authToken)

    const completeActiveSession = async (sessionId) => {
        if (!sessionId) {
            throw new Error('Missing session id – cannot complete session')
        }

        setIsCompleting(true)
        setCompletionError('')

        try {
            await completeSession(sessionId)
            currentSessionIdRef.current = null
            onSessionComplete?.(sessionId)
            setPhase('completed')
        } catch (error) {
            setCompletionError(error instanceof Error ? error.message : 'Failed to complete session')
        } finally {
            setIsCompleting(false)
        }
    }

    const {
        hours,
        seconds,
        minutes,
        isRunning,
        start,
        pause,
        resume,
        restart,
    } = useTimer({
        expiryTimestamp, autoStart: false, onExpire: () => {
            audio.play()
            sessionAudio.pause()

            if (phaseRef.current === 'break') {
                setSelectedBreakTask(null)
                setPhase('idle')
                return
            }

            setCompletedRounds((prev) => prev + 1)
            const completedSessionId = currentSessionIdRef.current
            if (!completedSessionId) {
                setCompletionError('Session finished locally but no session id was available to complete it.')
                return
            }

            void completeActiveSession(completedSessionId)
        }
    });
    const remainingSeconds = Math.max(0, (hours * 3600) + (minutes * 60) + seconds)

    useEffect(() => {
        if (hydrationInitializedRef.current) {
            return
        }
        hydrationInitializedRef.current = true

        const persistedState = readPersistedTimerState(POMODORO_STATE_STORAGE_KEY)

        if (!persistedState) {
            setHasHydratedState(true)
            return
        }

        setMuted(Boolean(persistedState.isMuted))
        setCompletedRounds(Number.isFinite(persistedState.completedRounds) && persistedState.completedRounds >= 0 ? Math.trunc(persistedState.completedRounds) : 0)
        setCompletionError(typeof persistedState.completionError === 'string' ? persistedState.completionError : '')

        const persistedSessionId = persistedState.currentSessionId ?? null
        currentSessionIdRef.current = persistedSessionId
        onSessionStart?.(persistedSessionId)

        const persistedPhase = ['idle', 'focus', 'break', 'completed'].includes(persistedState.phase) ? persistedState.phase : 'idle'
        const fallbackDuration = persistedPhase === 'break' ? safeBreakDurationSeconds : safeDefaultDurationSeconds
        const persistedDuration = Number.isFinite(persistedState.timerDurationSeconds) && persistedState.timerDurationSeconds > 0
            ? Math.trunc(persistedState.timerDurationSeconds)
            : fallbackDuration

        setTimerDurationSeconds(persistedDuration)

        if (persistedPhase === 'focus' || persistedPhase === 'break') {
            const persistedRunning = Boolean(persistedState.isRunning)
            let remaining = persistedRunning
                ? Math.ceil(((Number(persistedState.targetEndAt) || 0) - Date.now()) / 1000)
                : Math.trunc(Number(persistedState.remainingSeconds) || 0)

            if (!Number.isFinite(remaining) || remaining <= 0) {
                if (persistedPhase === 'focus') {
                    setPhase('completed')
                } else {
                    setPhase('idle')
                }
            } else {
                setPhase(persistedPhase)
                restart(createExpiryTimestamp(remaining), persistedRunning)
            }
        } else {
            setPhase(persistedPhase)
        }

        setHasHydratedState(true)
    }, [onSessionStart, restart, safeBreakDurationSeconds, safeDefaultDurationSeconds])

    useEffect(() => {
        if (!hasHydratedState) {
            return
        }

        const payload = {
            version: 1,
            phase,
            isMuted,
            isRunning,
            timerDurationSeconds,
            remainingSeconds: isRunning ? null : remainingSeconds,
            targetEndAt: isRunning ? Date.now() + (remainingSeconds * 1000) : null,
            completedRounds,
            completionError,
            currentSessionId: currentSessionIdRef.current ?? null,
            persistedAt: Date.now(),
        }

        try {
            localStorage.setItem(POMODORO_STATE_STORAGE_KEY, JSON.stringify(payload))
        } catch (error) {
            console.warn('Failed to persist pomodoro state:', error)
        }
    }, [completedRounds, completionError, hasHydratedState, isMuted, isRunning, phase, remainingSeconds, timerDurationSeconds])

    useEffect(() => {
        phaseRef.current = phase
    }, [phase])

    useEffect(() => {
        defaultDurationRef.current = safeDefaultDurationSeconds
    }, [safeDefaultDurationSeconds])

    useEffect(() => {
        if (!hasHydratedState) {
            return;
        }

        if (phase !== 'idle' && phase !== 'completed') {
            return;
        }

        const time = createExpiryTimestamp(safeDefaultDurationSeconds);
        setTimerDurationSeconds(safeDefaultDurationSeconds)
        restart(time, false)
    }, [hasHydratedState, phase, safeDefaultDurationSeconds, restart])

    const displayedMinutes = (hours * 60) + minutes
    const formattedMinutes = displayedMinutes < 10 ? `0${displayedMinutes}` : displayedMinutes
    const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds
    const progress = remainingSeconds / Math.max(timerDurationSeconds, 1)
    const radius = 46
    const circumference = 2 * Math.PI * radius
    const elapsedStrokeDashoffset = circumference * progress

    return (
        <div className='text-center'>
            <div className='mx-auto mb-6 flex h-[260px] w-[260px] items-center justify-center'>
                <div className='relative flex h-full w-full items-center justify-center'>
                    <svg className='absolute inset-0 h-full w-full -rotate-90' viewBox='0 0 100 100' aria-hidden='true'>
                        <circle
                            cx='50'
                            cy='50'
                            r={radius}
                            fill='none'
                            stroke='var(--border-color)'
                            strokeWidth='3'
                        />
                        <circle
                            cx='50'
                            cy='50'
                            r={radius}
                            fill='none'
                            stroke='var(--brand-lavender)'
                            strokeWidth='3'
                            strokeLinecap='round'
                        />
                        <circle
                            cx='50'
                            cy='50'
                            r={radius}
                            fill='none'
                            stroke='var(--button-primary-bg)'
                            strokeWidth='3'
                            strokeLinecap='round'
                            strokeDasharray={circumference}
                            strokeDashoffset={elapsedStrokeDashoffset}
                            className='transition-[stroke-dashoffset] duration-700 ease-out'
                        />
                    </svg>

                    <div
                        className='text-[72px] font-light leading-none tracking-[-0.05em] text-[var(--text-primary)] md:text-[88px]'
                        style={{fontVariantNumeric: 'tabular-nums'}}
                    >
                        <span>{formattedMinutes}</span>:<span>{formattedSeconds}</span>
                    </div>
                </div>
            </div>

            {!isRunning && (phase === 'idle' || phase === 'completed') && (
                <button className="inline-flex items-center gap-2 rounded-md border border-[var(--button-primary-bg)] bg-[var(--button-primary-bg)] px-4 py-2 text-sm font-semibold text-[var(--button-primary-text)] shadow-sm transition hover:brightness-105 active:brightness-95"
                        disabled={isCompleting || isSettingsLoading}

                        onClick={async () => {
                            try {
                                currentSessionIdRef.current = await startSession({
                                    taskId: task || null,
                                    projectId: project || null,
                                    planned_duration_seconds: safeDefaultDurationSeconds,
                                })
                                setCompletionError('')
                                onSessionStart?.(currentSessionIdRef.current)
                                setPhase('focus')
                                start()
                                if (!isMuted) {
                                    sessionAudio.play()
                                }
                            } catch (error) {
                                console.error('Session start failed:', error)
                            }
                        }}>{isSettingsLoading ? 'Loading...' : 'Start Session'}</button>)}

            {isRunning && (phase === 'focus' || phase === 'break') && (
                <button className="inline-flex items-center gap-2 rounded-md border border-[var(--button-primary-bg)] bg-[var(--button-primary-bg)] px-4 py-2 text-sm font-semibold text-[var(--button-primary-text)] shadow-sm transition hover:brightness-105 active:brightness-95"
                        onClick={() => {
                pause()
                sessionAudio.pause()
            }}>Pause</button>)}

            {!isRunning && (phase === 'focus' || phase === 'break') && remainingSeconds > 0 && (
                <button className="inline-flex items-center gap-2 rounded-md bg-[var(--surface-deep)] hover:bg-[var(--surface-hover)] active:bg-[var(--surface-hover)] px-4 py-2 text-sm font-medium text-[var(--text-primary)] border border-[var(--border-color)] shadow-sm shadow-black/40 transition"
                        disabled={isCompleting}
                        onClick={() => {
                    resume()
                    if (!isMuted) {
                        sessionAudio.play()
                    }
                }}>Continue</button>)}
            {isRunning && (phase === 'focus' || phase === 'break') && (
                <button className="inline-flex items-center gap-2 ml-4 rounded-md bg-[var(--surface-deep)] hover:bg-[var(--surface-hover)] active:bg-[var(--surface-hover)] px-4 py-2 text-sm font-medium text-[var(--text-primary)] border border-[var(--border-color)] shadow-sm shadow-black/40 transition"
                        disabled={isCompleting}

                        onClick={() => {
                    const time = createExpiryTimestamp(defaultDurationRef.current);
                    setTimerDurationSeconds(defaultDurationRef.current)
                    restart(time, false)
                    sessionAudio.pause()
                    currentSessionIdRef.current = null
                    setCompletedRounds(0)
                    setCompletionError('')
                    onSessionStart?.(null)
                    setPhase('idle');
                }}>Stop</button>)}

            {!isRunning && phase === 'focus' && isCompleting && (
                <p className="text-sm text-[var(--text-secondary)]">Saving completed session...</p>
            )}

            {!isRunning && phase === 'focus' && !isCompleting && completionError && (
                <div className="space-y-3">
                    <p className="text-sm text-red-300">{completionError}</p>
                    <button
                        className="inline-flex items-center gap-2 rounded-md bg-red-900/40 hover:bg-red-900/60 active:bg-red-900/70 px-4 py-2 text-sm font-medium text-red-100 border border-red-700/60 shadow-sm shadow-black/40 transition"
                        onClick={() => {
                            if (currentSessionIdRef.current) {
                                void completeActiveSession(currentSessionIdRef.current)
                            }
                        }}
                    >
                        Retry Completion
                    </button>
                </div>
            )}

            {phase === 'completed' && !isRunning && (
                <button className="ml-4 inline-flex items-center gap-2 rounded-md border border-[var(--button-primary-bg)] bg-[var(--button-primary-bg)] px-4 py-2 text-sm font-semibold text-[var(--button-primary-text)] shadow-sm transition hover:brightness-105 active:brightness-95"
                        onClick={() => {
                    const time = createExpiryTimestamp(safeBreakDurationSeconds);
                    setPhase('break')
                    setTimerDurationSeconds(safeBreakDurationSeconds)
                    restart(time, true)
                    if (!isMuted) {
                        sessionAudio.play()
                    }
                }}>Start Break</button>)}
            {(phase === 'completed' || phase === 'break') && breakTasks.length > 0 && (
                <div className="mt-6 text-left max-w-xs mx-auto">
                    <p className="mb-2 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">Break tasks</p>
                    <ul className="space-y-2">
                        {breakTasks.map((task, index) => {
                            const isSelected = selectedBreakTask === index;
                            return (
                                <li key={index}>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedBreakTask(isSelected ? null : index)}
                                        className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition ${
                                            isSelected
                                                ? 'border-[var(--button-primary-bg)] bg-[var(--button-primary-bg)] text-[var(--button-primary-text)]'
                                                : 'border-[var(--border-strong)] bg-[var(--surface-deep)] text-[var(--text-secondary)] hover:border-[var(--button-primary-bg)] hover:bg-[var(--surface-hover)]'
                                        }`}
                                    >
                                        {isSelected ? '✓ ' : ''}{task}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            <div>
                <button className="inline-flex items-center gap-2 mt-4 rounded-md bg-[var(--surface-deep)] hover:bg-[var(--surface-hover)] active:bg-[var(--surface-hover)] px-4 py-2 text-sm font-medium text-[var(--text-primary)] border border-[var(--border-color)] shadow-sm shadow-black/40 transition"
                        disabled={isCompleting || (isSettingsLoading && (phase === 'idle' || phase === 'completed'))}
                        onClick={() => {
                            const willBeMuted = !isMuted
                            setMuted(willBeMuted)
                            if (willBeMuted) {
                                sessionAudio.pause()
                            } else {
                                if (isRunning) {
                                    sessionAudio.play()
                                }
                            }
                        }}>
                    {isMuted ? <VolumeOffIcon/> : <VolumeUpIcon/>}
                </button>
            </div>
            {isSettingsLoading ? <p className="mt-3 text-xs text-[var(--text-muted)]">Loading pomodoro settings...</p> : null}
        </div>
    );
}

export default PomodoroTimer
